import Cookies from 'js-cookie';

/**
 * Cookie-consent storage.
 *
 * This is the single seam between the UI and where consent is persisted.
 * It stores the user's choice AND gates tracking: `hasTrackingConsent()` decides
 * whether the CMS analytics/marketing/personalization pixels are injected
 * (see utils/updatePixelScripts.js `pagePixels`), and `saveConsent` fires
 * `CONSENT_CHANGE_EVENT` so listeners can re-apply pixels the moment the user
 * changes their choice — no page reload.
 *
 * Stored as a cookie (not localStorage) so the record is readable client-side
 * now and by any future server/edge/consent-mode tooling, and gets native
 * expiry. Bump CONSENT_VERSION when the stored shape changes to re-prompt users.
 */

export const CONSENT_COOKIE = 'cookie_consent';
export const CONSENT_EXPIRY_DAYS = 180;
export const CONSENT_VERSION = 1;

// Window event dispatched on every saveConsent so tracking can be (re)applied live.
export const CONSENT_CHANGE_EVENT = 'cookieconsentchange';

// Window event that re-opens the preferences modal, so consent can be withdrawn
// as easily as it was granted (GDPR). Fired by `openCookiePreferences`, listened
// for by the CookieConsent banner. Any UI (e.g. a footer link) can trigger it.
export const CONSENT_OPEN_EVENT = 'cookieconsentopen';

/**
 * Re-open the cookie preferences modal from anywhere (footer link, etc.).
 */
export const openCookiePreferences = () => {
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(CONSENT_OPEN_EVENT));
    }
};

// Keys on the stored consent object that are not consent categories.
const CONSENT_META_KEYS = ['necessary', 'version', 'timestamp'];

/**
 * Returns the stored consent object, or null if none / invalid / outdated.
 */
export const getConsent = () => {
    const raw = Cookies.get(CONSENT_COOKIE);
    if (!raw) return null;

    try {
        const parsed = JSON.parse(raw);
        if (!parsed || parsed.version !== CONSENT_VERSION) return null;
        return parsed;
    } catch (error) {
        return null;
    }
};

/**
 * True when a valid consent record already exists (banner should stay hidden).
 */
export const hasConsent = () => getConsent() !== null;

/**
 * True when the user has consented to at least one non-essential (tracking)
 * category — the gate for injecting the CMS pixels. No consent record yet
 * (banner still showing) counts as no tracking. Category-shape-agnostic: any
 * stored category outside CONSENT_META_KEYS that is `true` grants tracking.
 */
export const hasTrackingConsent = () => {
    const consent = getConsent();
    if (!consent) return false;
    return Object.keys(consent).some(
        (key) => !CONSENT_META_KEYS.includes(key) && consent[key] === true
    );
};

/**
 * Remove the stored consent record entirely (e.g. when the project disables
 * the cookie popup via `projectInfo.cookiePopupEnabled`). Fires the same
 * change event so tracking pixels are torn down immediately.
 */
export const clearConsent = () => {
    Cookies.remove(CONSENT_COOKIE);

    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(CONSENT_CHANGE_EVENT, { detail: null }));
    }
};

/**
 * Persist the user's per-category preferences. `necessary` is always forced on.
 * `prefs` is a flat map, e.g. { necessary: true, analytics: false, ... }.
 */
export const saveConsent = (prefs) => {
    const consent = {
        ...prefs,
        necessary: true,
        version: CONSENT_VERSION,
        timestamp: new Date().toISOString(),
    };

    Cookies.set(CONSENT_COOKIE, JSON.stringify(consent), {
        expires: CONSENT_EXPIRY_DAYS,
    });

    // Let listeners (re)apply tracking to match the new choice, without a reload.
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(CONSENT_CHANGE_EVENT, { detail: consent }));
    }

    return consent;
};

/**
 * Build a preferences map from the CMS category list.
 * `allOn` sets every category on (Accept All); otherwise only the
 * always-active categories (e.g. necessary) are enabled (Reject All).
 */
export const buildPrefs = (categories = [], allOn = false) =>
    categories.reduce((acc, category) => {
        acc[category.key] = allOn || Boolean(category.alwaysActive);
        return acc;
    }, {});
